import axios from "axios";
import { NativeBiometric } from "capacitor-native-biometric";
import constants from "@/helpers/constants";
import { Device } from "@capacitor/device";

const CREDENTIALS_SERVER = "https://cabinet.nomad.kz/";

const VERIFY_IDENTITY_OPTIONS = {
  reason: "Для удобного и быстрого входа",
  title: "Подтверждение личности",
  subtitle: "Используйте биометрию для входа",
  description: "Подтвердите свою личность для защиты данных",
};

export const BIOMETRIC_CREDENTIALS_NOT_FOUND = "MOBILE_BIOMETRIC_CREDENTIALS_NOT_FOUND";

const BIOMETRICS_UNAVAILABLE = "MOBILE_BIOMETRIC_BIOMETRICS_UNAVAILABLE";
const UNKNOWN_BIOMETRIC_ERROR = "MOBILE_BIOMETRIC_UNKNOWN_ERROR";

function biometricErrorKey(code) {
  return constants.biometricAuthErrors[code] || UNKNOWN_BIOMETRIC_ERROR;
}

function unavailableBiometryErrorKey(code) {
  return code ? biometricErrorKey(code) : BIOMETRICS_UNAVAILABLE;
}

export class MobileBiometricException extends Error {
  constructor(message, response = null) {
    super(message);
    this.name = "MobileBiometricException";
    this.response = response;
    Error.captureStackTrace(this, this.constructor);
  }
}

export class MobileBiometricService {
  async registrationBiometry(iin, deviceId) {
    this.ensureUserIin(iin);

    await this.verifyIdentity("registrationBiometry");

    return this.savePinCodeHash(iin, deviceId);
  }

  async savePinCodeHash(iin, deviceId) {
    this.ensureUserIin(iin);

    try {
      const hashPin = await this.getHashPinCode(deviceId);

      if (hashPin && hashPin.hash) {
        await NativeBiometric.setCredentials({
          username: String(iin),
          password: hashPin.hash,
          server: CREDENTIALS_SERVER,
        });
        return true;
      }

      return false;
    } catch (error) {
      this.sendErrorLog(error, "savePinCodeHash");
      const isOffline =
        !navigator.onLine ||
        (error?.isAxiosError &&
          (error.code === "ERR_NETWORK" ||
            error.message === "Network Error"));

      if (isOffline) {
        throw new MobileBiometricException("NETWORK_ERROR");
      }
      throw new MobileBiometricException(
        "MOBILE_BIOMETRIC_SET_CREDENTIALS_ERROR"
      );
    }
  }

  async deleteBiometry() {
    await NativeBiometric.deleteCredentials({
      server: CREDENTIALS_SERVER,
    });

    return true;
  }

  async startVerification(iin, deviceId) {
    const pinCodeHash = await this.getSavedPinCodeHash(iin);

    await this.verifyIdentity("startVerification");

    try {
      const result = await this.checkHashPinCode(pinCodeHash, deviceId);

      if (result) {
        return result.mobile_token;
      }

      throw new MobileBiometricException(
        "MOBILE_BIOMETRIC_SET_CREDENTIALS_ERROR"
      );
    } catch (error) {
      const isOffline =
        !navigator.onLine ||
        (error?.isAxiosError &&
          (error.code === "ERR_NETWORK" ||
            error.message === "Network Error"));

      if (isOffline) {
        throw new MobileBiometricException("NETWORK_ERROR");
      }
      throw new MobileBiometricException(
        "MOBILE_BIOMETRIC_SET_CREDENTIALS_ERROR"
      );
    }
  }

  async verifyIdentity(method) {
    await this.ensureBiometryAvailable(method);

    try {
      await NativeBiometric.verifyIdentity(VERIFY_IDENTITY_OPTIONS);
    } catch (error) {
      this.sendErrorLog(error, method);
      throw new MobileBiometricException(biometricErrorKey(error.code));
    }
  }

  async ensureBiometryAvailable(method) {
    const availability = await NativeBiometric.isAvailable();

    if (availability.isAvailable) return;

    this.sendErrorLog(new Error(`Biometry is unavailable, code ${availability.errorCode}`), method);
    throw new MobileBiometricException(unavailableBiometryErrorKey(availability.errorCode));
  }

  ensureUserIin(iin) {
    if (!iin) {
      throw new MobileBiometricException(UNKNOWN_BIOMETRIC_ERROR);
    }
  }

  async getSavedPinCodeHash(iin) {
    this.ensureUserIin(iin);

    const credentials = await NativeBiometric.getCredentials({ server: CREDENTIALS_SERVER })
      .catch(() => null);

    if (!credentials || credentials.username !== String(iin)) {
      throw new MobileBiometricException(BIOMETRIC_CREDENTIALS_NOT_FOUND);
    }

    return credentials.password;
  }

  /**
   * Проверяет, существует ли PIN-код для текущего пользователя и может ли он его изменить.
   *
   * @returns {Promise<{exist: boolean, can_change: boolean}>} Объект с информацией о наличии PIN-кода и возможности его изменения.
   *
   * @throws {MobileBiometricException} Бросает исключение, если не удалось получить информацию о наличии PIN-кода.
   */
  async getPinCodeAvailability(deviceId) {
    const response = await axios.get(
      `/mobile-app/native-biometry/pin/exist?device_id=${deviceId}`
    );

    if (!response.data.success) {
      console.error();
      throw new MobileBiometricException(
        "Не получен ответ существования PIN-код",
        response
      );
    }

    return response.data.exist;
  }

  /**
   * Устанавливает PIN-код.
   *
   * @param {Object} data - Данные для установки PIN-кода.
   * @param {string} data.pin - Значение нового PIN-кода.
   *
   * @returns {Promise<boolean>} Возвращает `true`, если установка PIN-кода прошла успешно
   *
   * @throws {MobileBiometricException} Бросает исключение, если не удалось получить информацию о наличии PIN-кода.
   */
  async setPinCode(data) {
    try {
      const response = await axios.post(
        "/mobile-app/native-biometry/pin/create",
        data
      );

      if (!response.data.success) {
        throw new MobileBiometricException(
          response.data.message || "Ошибка при установке PIN-кода",
          response
        );
      }

      return response.data;
    } catch (error) {
      throw new MobileBiometricException(
        error.response.data.message || "Ошибка при установке PIN-кода",
        error
      );
    }
  }

  async getHashPinCode(device_id) {
    const response = await axios.get(
      `/mobile-app/native-biometry/pin/get-hash?device_id=${device_id}`
    );

    return response.data;
  }

  async checkPinCode(data) {
    const response = await axios.post(
      "/mobile-app/native-biometry/pin/check",
      data
    );

    return response.data;
  }

  async onlyCheckPinCode(data) {
    const response = await axios.post(
      "/mobile-app/native-biometry/pin/only-check",
      data
    );

    return response.data;
  }

  async checkHashPinCode(pinCodeHash, deviceId) {
    const response = await axios.post(
      "/mobile-app/native-biometry/pin/check-hash",
      {
        pin_code_hash: pinCodeHash,
        device_id: deviceId,
      }
    );

    return response.data;
  }

  async isTester() {
    const response = await axios.get("/mobile-app/native-biometry/is-tester");

    return response.data;
  }

  // Отправить логи на сервер
  async sendErrorLog(error, method) {
    const deviceInfo = await Device.getInfo();
    const response = await axios.post("/mobile-app/native-biometry/debug-log", {
      error: error.code ? `[${error.code}] ${error.message}` : error.message,
      method: method,
      stack: error.stack,
      device_info: JSON.stringify(deviceInfo),
    });

    return response.data;
  }

  async changePinCode(data) {
    const response = await axios.post(
      "/mobile-app/native-biometry/pin/change",
      data
    );

    return response.data;
  }
}

export default MobileBiometricService;
